import moment from 'moment';
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CollectTrackingResultType } from '../../../enums/CollectTrackingResultType';
import { ConsultTrackingResultType } from '../../../enums/ConsultTrackingResultType';
import { OrderGeneralOption } from '../../../enums/OrderGeneralOption';
import { Session } from '../../../models/Session';
import { StateLog } from '../../../models/StateLog';
import { strings } from '../../../resources/strings';
import ServicesContext from '../../../contexts/ServicesContext';
import { SessionHelper } from '../../../helpers/SessionHelper';
import { OrderInTransport } from '../../../services/Orders/GetOrdersInTransport/OrderInTransport';
import { SaveStateLogInput } from '../../../services/StatesOrders/SaveStateLog/SaveStateLogInput';
import { DropdownButton, DropdownButtonColor, DropdownButtonDirection } from '../../../common-components/DropdownButton/DropdownButton';
import { IWidthColumnOrdersInTransportTable } from './useOrdersInTransportTable';

interface IOrdersInTransportTableRowProps {
  orderInTransport: OrderInTransport;
  widthColumn: IWidthColumnOrdersInTransportTable;
  session?: Session | null;
  handleGeneralOption: (orderInTransport: OrderInTransport, key: any) => void;
  newStateOrder?: (newState: StateLog) => void;
}

const OrdersInTransportTableRow: React.FC<IOrdersInTransportTableRowProps> = ({
  orderInTransport,
  widthColumn,
  session,
  handleGeneralOption,
  newStateOrder
}) => {

  const {
    statesService,
    saveStateLogService,
    toastService,
    alertService
  } = React.useContext(ServicesContext);

  const getDeliveryNextDropdownItems = (currentStateId?: number) => statesService.getDeliveryNext(currentStateId).map(state => {
    const item = {
      key: state?.id,
      text: state?.name
    };
    return item;
  });

  const genericalOptionsItems = [
    {
      key: OrderGeneralOption.Historic,
      text: strings.Historic
    }
  ];

  if (statesService.stateIdIsDeliverable(orderInTransport.currentState.currentStateId)) {
    genericalOptionsItems.push({
      key: OrderGeneralOption.ManageTransport,
      text: strings.ManageTransport
    });
  }

  const genericalOptionsRender = (
    <DropdownButton
      items={genericalOptionsItems}
      defaultStyle={true}
      small={true}
      buttonColor={DropdownButtonColor.Secondary}
      text={''}
      clickItem={(key) => handleGeneralOption(orderInTransport, key)}
      marginRight={'3px'}
    />
  );

  let virtualCollect = 'Não realizada até o momento.';
  const collectTrackingDate = orderInTransport?.tracking?.collectTrackingProcessedAt || orderInTransport?.tracking?.collectTrackingCreatedAt;
  const virtualCollectDate = collectTrackingDate
    ? moment(collectTrackingDate).format('DD/MM')
    : '';
  if (orderInTransport?.tracking?.collectTrackingResultType === CollectTrackingResultType.Success) {
    virtualCollect = `Realizada com sucesso ${virtualCollectDate}.`;
  }
  if (orderInTransport?.tracking?.collectTrackingResultType === CollectTrackingResultType.Failed) {
    virtualCollect = `Falhou na data ${virtualCollectDate}.`;
  }
  if (orderInTransport?.tracking?.collectTrackingResultType === CollectTrackingResultType.PendingUser) {
    virtualCollect = 'Pendente de realização manual.';
  }
  if (orderInTransport?.tracking?.collectTrackingResultType === CollectTrackingResultType.PendingSystem) {
    virtualCollect = 'Pendente de realização automática.';
  }

  let lastConsult = 'Nenhuma até o momento.';
  const consultTrackingDate = orderInTransport?.tracking?.lastConsultTrackingLogFinishedAt
    ? moment(orderInTransport.tracking.lastConsultTrackingLogFinishedAt).format('DD/MM')
    : '';
  const consultTrackingHours = orderInTransport?.tracking?.lastConsultTrackingLogFinishedAt
    ? moment(orderInTransport.tracking.lastConsultTrackingLogFinishedAt).format('HH') + 'h de '
    : '';
  if (orderInTransport?.tracking?.lastConsultTrackingLogResultType === ConsultTrackingResultType.Success) {
    lastConsult = `Realizada com sucesso ${consultTrackingHours} ${consultTrackingDate}.`;
  }
  if (orderInTransport?.tracking?.lastConsultTrackingLogResultType === ConsultTrackingResultType.Failed) {
    lastConsult = `Realizada ${consultTrackingHours} ${consultTrackingDate} falhou.`;
  }

  let carrierName = orderInTransport.carrier?.name;
  const carrierCode = orderInTransport.carrier?.code || '';
  switch (carrierCode) {
    case 'EXP':
      carrierName = 'Total Express';
      break;
    case 'LOG_VIP':
      carrierName = 'Loggi';
      break;
    default:
      carrierName = orderInTransport.carrier?.name;
  }

  let backgroundColor = '#ababe3';
  let tooltipText = ''; 
  if (orderInTransport) {
    if (orderInTransport.isCrossChannelSale) {
      backgroundColor = '#7979c1';
    }
    if (orderInTransport.locationId === 1) {
      tooltipText = 'Entregue por e-commerce';
    } else {
      tooltipText = 'Entregue por loja';
    }
    if (orderInTransport.isCrossChannelSale && orderInTransport.originLocationId) {
      tooltipText += ` e com origem na loja ${orderInTransport.originLocationName || ' de ID ' + orderInTransport.originLocationId}`;
    }
  }

  return (
    <tr key={`order-${orderInTransport.orderId}`} data-order-number={orderInTransport.orderNumber} data-order-id={orderInTransport.orderId}>
      <td style={{ width: widthColumn.FulfilledBy, minWidth: widthColumn.FulfilledBy, maxWidth: widthColumn.FulfilledBy }}>
        <OverlayTrigger
          placement="right"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip>{tooltipText}</Tooltip>}
        >
          <span style={{ color: '#fff', backgroundColor: backgroundColor,
            borderRadius: '2px', height: '16px', maxHeight: '16px', 
            width: '14px', minWidth: '14px', display: 'flex', 
            justifyContent: 'center', alignItems: 'center',
            fontSize: '0.65em'
          }}
          >
            {orderInTransport.locationId === 1 ? 'E' : 'L'}
          </span>
        </OverlayTrigger>
      </td>
      <td style={{ width: widthColumn.OrderNumber, minWidth: widthColumn.OrderNumber, maxWidth: widthColumn.OrderNumber }}>
        {orderInTransport.orderNumber}
      </td>
      <td style={{ width: widthColumn.CarrierName, minWidth: widthColumn.CarrierName }}>
        {carrierName}
      </td>
      <td style={{ width: widthColumn.ClientName }}>
        {orderInTransport.customerName}
      </td>
      <td style={{ width: widthColumn.CurrentStateDescription }}>
        {orderInTransport.currentState?.createdAt ? moment(orderInTransport.currentState?.createdAt).format('DD/MM') + ' - ' : ''}
        {orderInTransport.currentState?.description}
      </td>
      <td style={{ width: widthColumn.VirtualCollect }}>
        {virtualCollect}
      </td>
      <td style={{ width: widthColumn.LastConsult }}>
        {lastConsult}
      </td>
      <td style={{ width: widthColumn.LastStatusPackage }}>
        {orderInTransport?.tracking?.lastCollectTrackingItemStatusDescription || 'Nenhum até o momento.'}
      </td>
      <td style={{ textAlign: 'right', width: widthColumn.Options, minWidth: widthColumn.Options }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignContent: 'center', alignItems: 'center' }}>
          {genericalOptionsRender}
          {SessionHelper.canManageStateTransport(session) ?
            <DropdownButton
              items={getDeliveryNextDropdownItems(orderInTransport.currentState.currentStateId)}
              defaultStyle={true}
              small={true}
              direction={DropdownButtonDirection.DropToRight}
              buttonColor={DropdownButtonColor.Success}
              text={' '}
              clickItem={(key) => changeState(orderInTransport, key)}
              marginLeft={'3px'}
            />
          : null}
        </div>
      </td>
    </tr>
  );

  function changeState(orderInTransport: OrderInTransport, id: any) {
    var input = new SaveStateLogInput();
    input.orderId = orderInTransport.orderId;
    input.newStateId = id;
    input.userId = session?.user?.id || 0;
    saveStateLogService.handle(input)
      .then(result => {
        if (!result?.value?.result) return;
        const { stateId, previousStateId, createdAt, orderId } = result.value;
        const newStateLog = new StateLog();
        newStateLog.currentStateId = stateId;
        newStateLog.previousStateId = previousStateId;
        newStateLog.orderId = orderId;
        newStateLog.createdAt = createdAt || new Date();
        newStateLog.currentStateName = statesService.get().find(x => x.id === stateId)?.name ||
          statesService.getDelivery().find(x => x.id === stateId)?.name || '';
        if (newStateOrder) newStateOrder(newStateLog);
      })
      .catch(error => {
        if (error.response) {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
          return;
        }
        alertService.warning(strings.TheServerIsNotRespondingRefreshListAndTryAgain);
      });
  }
}

export { OrdersInTransportTableRow };